import React from "react";
import { useSettings } from "@/context/SettingsContext";
import { IMAGES } from "@/constants/images";
import PageHero from "@/components/public/PageHero";
import SectionHeading from "@/components/public/SectionHeading";
import CTASection from "@/components/public/CTASection";
import Reveal from "@/components/public/Reveal";
import { Package, FileText, Truck, Container, Ship, ClipboardList } from "lucide-react";

const WORKFLOW = [
  { icon: ClipboardList, title: "Coffee Preparation", desc: "Green coffee prepared to agreed specifications before shipment." },
  { icon: Package, title: "Packaging", desc: "Packed in agreed formats such as GrainPro liners and jute bags." },
  { icon: FileText, title: "Documentation", desc: "Export documentation prepared as required for international trade." },
  { icon: Truck, title: "Freight Coordination", desc: "Coordination of trucking and inland movement to the port." },
  { icon: Container, title: "Container Loading", desc: "Container stuffing and loading coordination." },
  { icon: Ship, title: "International Shipping", desc: "Coordination of ocean freight to the buyer's destination." },
];

export default function ExportLogistics() {
  const { settings } = useSettings();
  const details = [
    ["Port of Loading", settings.port_of_loading],
    ["Packaging Options", settings.packaging_options],
    ["Bag Size", settings.bag_size],
    ["Container Options", settings.container_options],
    ["MOQ", settings.moq],
    ["Lead Time", settings.lead_time],
    ["Incoterms", settings.incoterms],
    ["Payment Terms", settings.payment_terms],
  ];
  return (
    <div>
      <PageHero eyebrow="Logistics" title="Export & Logistics" sub="A transparent look at how we prepare, document and coordinate international coffee shipments." image={IMAGES.port} />
      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {WORKFLOW.map((w, i) => (
            <Reveal key={w.title} delay={(i % 3) * 90}>
              <div className="h-full rounded-xl border border-forest/10 bg-white p-7">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-forest text-offwhite"><w.icon className="h-6 w-6" /></div>
                <h3 className="font-display text-lg font-bold text-forest-dark">{w.title}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-espresso/65">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="bg-beige py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <SectionHeading eyebrow="Commercial Details" title="Logistics Information" sub="Details are shown when confirmed. Anything not yet provided is available upon quotation." />
          <div className="mt-10 overflow-hidden rounded-xl border border-forest/10 bg-white">
            <table className="w-full text-left">
              <tbody>
                {details.map(([label, val], i) => (
                  <tr key={label} className={i % 2 ? "bg-beige/40" : "bg-white"}>
                    <td className="w-1/2 px-6 py-4 font-mono text-xs uppercase tracking-wider text-espresso/55 sm:w-1/3">{label}</td>
                    <td className="px-6 py-4 font-body text-sm font-medium text-forest-dark">{val || <span className="italic text-espresso/45">Available upon quotation.</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
